import { c as _c } from "react/compiler-runtime";
import { RadioGroup } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioItem } from '../app-ui/RadioGroupItem';
import { ValueSelector } from '../app-ui/value-selector/ValueSelector';
import { useCallback, useEffect, useMemo } from 'react';
import { NumericSelector } from '../app-ui/numeric-selector/NumericSelector';
export enum TargetSelectorUi {
  ValueSelector = 'value-selector',
  HtmlSelect = 'html-select',
  NumericSelector = 'numeric-selector',
  UniqueTargetSelector = 'unique-target-selector',
  CustomComponentTargetSelector = 'custom-component-target-selector',
}
export interface TargetSelectorTarget<Target> {
  value: Target;
  label: string;
}
export interface TargetSelectorModeBaseOptions {
  placeholder?: string;
  selectorUi: TargetSelectorUi;
}
export interface MultipleTargetSelectorOptions<Target extends string> extends TargetSelectorModeBaseOptions {
  selectorUi: TargetSelectorUi.ValueSelector | TargetSelectorUi.HtmlSelect;
  targets: TargetSelectorTarget<Target>[];
}
export interface NumericTargetSelectorOptions extends TargetSelectorModeBaseOptions {
  selectorUi: TargetSelectorUi.NumericSelector;
  minValue: number;
  maxValue: number;
  maxCharacters?: number;
  step: number;
  labelFormatter?: (value: number) => string;
}
export interface UniqueTargetSelectorOptions<Target> extends TargetSelectorModeBaseOptions {
  selectorUi: TargetSelectorUi.UniqueTargetSelector;
  target: Target;
}
export interface CustomComponentTargetSelectorOptions<Target> extends TargetSelectorModeBaseOptions {
  selectorUi: TargetSelectorUi.CustomComponentTargetSelector;
  componentFactory: (currentTarget: Target | null, onTargetChange: (target: Target) => void) => React.ReactNode;
}
interface TargetSelectorModeBase<Mode extends string> {
  label: string;
  description?: string;
  isDisabled?: boolean;
  disabledReason?: string;
  /** The equation the mode solves, shown in mono under its control. */
  formula?: string;
  /** One line on what the mode depends on elsewhere in the design. */
  hint?: string;
  value: Mode;
}
export type TargetSelectorMode<Mode extends string, Target> = (TargetSelectorModeBase<Mode> & {
  options: MultipleTargetSelectorOptions<Extract<Target, string>>;
}) | (TargetSelectorModeBase<Mode> & {
  options: NumericTargetSelectorOptions;
}) | (TargetSelectorModeBase<Mode> & {
  options: UniqueTargetSelectorOptions<Target>;
}) | (TargetSelectorModeBase<Mode> & {
  options: CustomComponentTargetSelectorOptions<Target>;
});
export interface TargetSelectorProps<Mode extends string, Target> {
  title: string;
  modes: TargetSelectorMode<Mode, Target>[];
  selectedMode: Mode | null;
  selectedTarget: Target | null;
  shouldDisplayModeSelector?: boolean;
  onModeChange: (mode: Mode) => void;
  onTargetChange: (target: Target) => void;
}
export const TargetSelector = props => {
  const $ = _c(35);
  const {
    title,
    modes,
    onModeChange,
    onTargetChange,
    selectedMode: selectedModeValue,
    selectedTarget: selectedTargetValue,
    shouldDisplayModeSelector: t0
  } = props;
  const shouldDisplayModeSelector = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== modes || $[1] !== selectedModeValue) {
    t1 = selectedModeValue ? modes.find(mode => mode.value === selectedModeValue) : undefined;
    $[0] = modes;
    $[1] = selectedModeValue;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedMode = t1;
  let t2;
  bb0: {
    if (selectedTargetValue && selectedMode && (selectedMode.options.selectorUi === TargetSelectorUi.ValueSelector || selectedMode.options.selectorUi === TargetSelectorUi.HtmlSelect)) {
      let t3;
      if ($[3] !== selectedMode.options.targets || $[4] !== selectedTargetValue) {
        let t4;
        if ($[6] !== selectedTargetValue) {
          t4 = choice => choice.value === selectedTargetValue;
          $[6] = selectedTargetValue;
          $[7] = t4;
        } else {
          t4 = $[7];
        }
        t3 = selectedMode.options.targets.find(t4);
        $[3] = selectedMode.options.targets;
        $[4] = selectedTargetValue;
        $[5] = t3;
      } else {
        t3 = $[5];
      }
      t2 = t3;
      break bb0;
    }
    t2 = undefined;
  }
  const selectedTarget = t2;
  let t3;
  let t4;
  if ($[8] !== onTargetChange || $[9] !== selectedMode || $[10] !== selectedTargetValue) {
    t3 = () => {
      if (selectedMode && selectedMode.options.selectorUi === TargetSelectorUi.UniqueTargetSelector && selectedTargetValue !== selectedMode.options.target) {
        onTargetChange(selectedMode.options.target);
      }
    };
    t4 = [onTargetChange, selectedMode, selectedTargetValue];
    $[8] = onTargetChange;
    $[9] = selectedMode;
    $[10] = selectedTargetValue;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== onModeChange) {
    t5 = mode_0 => {
      onModeChange(mode_0);
    };
    $[13] = onModeChange;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const onModeChangeHandler = t5;
  let t6;
  if ($[15] !== onTargetChange || $[16] !== selectedMode || $[17] !== selectedTarget?.label || $[18] !== selectedTarget?.value || $[19] !== selectedTargetValue) {
    t6 = selectedMode ? <>{selectedMode.options.selectorUi === TargetSelectorUi.HtmlSelect && <Select value={selectedTarget?.value as unknown as string || ""} onValueChange={onTargetChange as unknown as (value: string) => void}><SelectTrigger className="w-full"><SelectValue placeholder={selectedMode.options.placeholder}>{selectedTarget?.label}</SelectValue></SelectTrigger><SelectContent>{selectedMode.options.targets.map(_temp)}</SelectContent></Select>}{selectedMode.options.selectorUi === TargetSelectorUi.ValueSelector && <ValueSelector currentValue={selectedTarget?.value as unknown as string} onChange={onTargetChange as unknown as (value: string) => void} values={selectedMode.options.targets.map(_temp2)} />}{selectedMode.options.selectorUi === TargetSelectorUi.NumericSelector ? <NumericSelector value={selectedTargetValue as unknown as number ?? selectedMode.options.minValue} onChange={onTargetChange as unknown as (value: number) => void} maxCharacters={selectedMode.options.maxCharacters} minValue={selectedMode.options.minValue} maxValue={selectedMode.options.maxValue} step={selectedMode.options.step} labelFormatter={selectedMode.options.labelFormatter} /> : null}{selectedMode.options.selectorUi === TargetSelectorUi.CustomComponentTargetSelector && selectedMode.options.componentFactory(selectedTargetValue, onTargetChange)}</> : null;
    $[15] = onTargetChange;
    $[16] = selectedMode;
    $[17] = selectedTarget?.label;
    $[18] = selectedTarget?.value;
    $[19] = selectedTargetValue;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const control = t6;
  let t7;
  if ($[21] !== title) {
    t7 = <h2 className="m-0 font-serif text-[30px] leading-none font-medium">{title}</h2>;
    $[21] = title;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== modes || $[24] !== onModeChangeHandler || $[25] !== selectedMode?.value || $[26] !== shouldDisplayModeSelector) {
    t8 = shouldDisplayModeSelector ? <RadioGroup onValueChange={onModeChangeHandler} value={selectedMode?.value} className="grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-2.5">{modes.map(_temp3)}</RadioGroup> : null;
    $[23] = modes;
    $[24] = onModeChangeHandler;
    $[25] = selectedMode?.value;
    $[26] = shouldDisplayModeSelector;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== control || $[29] !== selectedMode) {
    t9 = selectedMode && selectedMode.options.selectorUi !== TargetSelectorUi.UniqueTargetSelector ? <div role="group" aria-label={selectedMode.options.placeholder ?? selectedMode.label} data-slot="target-controls" className="flex flex-wrap items-end gap-x-8 gap-y-3 rounded-md border border-ptl-rule-strong bg-card px-4 py-3.5"><div className="flex flex-col items-start gap-2">{selectedMode.options.placeholder ? <span className="text-sm font-semibold">{selectedMode.options.placeholder}</span> : null}{control}</div>{selectedMode.formula || selectedMode.hint ? <div className="flex max-w-[60ch] flex-col gap-1 pb-1">{selectedMode.formula ? <span className="font-mono text-[13px] text-foreground">{selectedMode.formula}</span> : null}{selectedMode.hint ? <span className="text-[13px] leading-[1.5] text-muted-foreground">{selectedMode.hint}</span> : null}</div> : null}</div> : null;
    $[28] = control;
    $[29] = selectedMode;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t7 || $[32] !== t8 || $[33] !== t9) {
    t10 = <section className="flex flex-col gap-4">{t7}{t8}{t9}</section>;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  return t10;
};
function _temp(target) {
  return <SelectItem key={target.value as unknown as string} value={target.value as unknown as string}>{target.label}</SelectItem>;
}
function _temp2(target_0) {
  return {
    label: target_0.label,
    value: target_0.value as unknown as string
  };
}
function _temp3(mode_1) {
  return <RadioItem isDisabled={mode_1.isDisabled} disabledReason={mode_1.disabledReason} hasDescriptionOnDemand={true} key={mode_1.value} label={mode_1.label} description={mode_1.description ?? ""} value={mode_1.value} />;
}